import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

// Hardcoded admin credentials (in production, use a real backend)
const ADMIN_CREDENTIALS = {
  username: 'admin',
  password: 'KOA@2024',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = window.location.hostname === 'localhost' 
    ? 'http://localhost:5000/api' 
    : 'https://mediumpurple-squirrel-426661.hostingersite.com/api';

  useEffect(() => {
    const stored = sessionStorage.getItem('koa_admin');
    const token = localStorage.getItem('koa_token'); // Use the same token key as storefront if we want, or koa_admin_token
    if (stored && token) {
      try { setUser(JSON.parse(stored)); } catch {}
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    // 1. Local check for demo credentials (optional fallback)
    const isDemo = username === ADMIN_CREDENTIALS.username && password === ADMIN_CREDENTIALS.password;

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: username, password })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        // Even if backend fails, if it's the demo credentials, we could allow local access 
        // BUT it's better to require backend success for the token.
        return { success: false, error: data.error || 'Login failed' };
      }

      // If backend success, ensure role is admin for the 'admin' user
      // Sometimes backend might not return role or return it differently
      const role = data.role || (username === 'admin' ? 'admin' : 'user');
      
      if (role !== 'admin') {
        return { success: false, error: 'Not authorized as an admin' };
      }

      const userData = { ...data, role: 'admin', loginAt: Date.now() };
      setUser(userData);
      sessionStorage.setItem('koa_admin', JSON.stringify(userData));
      localStorage.setItem('koa_token', data.token); 
      return { success: true };
    } catch (err) {
      // Final fallback for demo user if network is down (will have no token, so some APIs might fail)
      if (isDemo) {
        const demoData = { 
          name: 'Demo Admin', 
          username: 'admin', 
          role: 'admin', 
          token: 'demo_token',
          loginAt: Date.now() 
        };
        setUser(demoData);
        sessionStorage.setItem('koa_admin', JSON.stringify(demoData));
        return { success: true };
      }
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('koa_admin');
    localStorage.removeItem('koa_token');
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, API_BASE, token: localStorage.getItem('koa_token') }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
